import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Globe, Download, CheckCircle2, XCircle } from 'lucide-react';
import ReviewCommentsCallout from '@/components/review/ReviewCommentsCallout';
import { exportProjectPdf } from '@/services/pdfExportService';
import { ciaLabels, getCiaLabel } from '@/lib/constants';
import { toast } from 'sonner';
import DomainHealthGrid from './DomainHealthGrid';

export default function ProjectInfoTab() {
  const { projectInfo, diagrams, threats, currentDiagramIndex, currentDiagram, fileName, fileSize } = useApp();
  const { questions, quickScanResults } = useQuestions();
  const reviewIndex = useReviewCommentsIndex();
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const handleExportPdf = async () => {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      await exportProjectPdf({
        isProject: true, projectInfo, diagrams, currentDiagramIndex,
        currentDiagram, threats, fileName, fileSize,
      });
    } catch {
      toast.error('Unable to export PDF. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (!projectInfo) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">
            No project information available. This data is only present in full project exports.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <ReviewCommentsCallout comments={reviewIndex.projectVersion} />
      {/* Actions */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={isExportingPdf}>
          <Download className="mr-2 h-4 w-4" />
          {isExportingPdf ? 'Exporting...' : 'Export PDF'}
        </Button>
      </div>

      {/* Two Column Layout */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* CIA Classification */}
        <Card>
          <CardHeader>
            <CardTitle>CIA Classification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-muted-foreground">Confidentiality</div>
                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                  ciaLabels[projectInfo.confidentiality ?? '']?.color || 'bg-gray-100 text-gray-800'
                }`}>
                  {getCiaLabel(projectInfo.confidentiality)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-muted-foreground">Integrity</div>
                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                  ciaLabels[projectInfo.integrity ?? '']?.color || 'bg-gray-100 text-gray-800'
                }`}>
                  {getCiaLabel(projectInfo.integrity)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-muted-foreground">Availability</div>
                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                  ciaLabels[projectInfo.availability ?? '']?.color || 'bg-gray-100 text-gray-800'
                }`}>
                  {getCiaLabel(projectInfo.availability)}
                </span>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Internet Facing
                </div>
                {projectInfo.internet_facing == null
                  ? <span>—</span>
                  : projectInfo.internet_facing
                    ? <CheckCircle2 aria-label="Yes" className="h-5 w-5 text-green-600 dark:text-green-400" />
                    : <XCircle aria-label="No" className="h-5 w-5 text-red-600 dark:text-red-400" />
                }
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ownership & Contacts */}
        <Card>
          <CardHeader>
            <CardTitle>Ownership & Contacts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-muted-foreground">Responsible ISO</div>
              <div className="text-sm text-right font-medium">{projectInfo.responsible_iso || 'Not specified'}</div>
            </div>

            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-muted-foreground">Technical Owner</div>
              <div className="text-sm text-right font-medium">{projectInfo.technical_owner || 'Not specified'}</div>
            </div>

            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-muted-foreground">Primary Contact</div>
              <div className="text-sm text-right font-medium">{projectInfo.primary_point_of_contact || 'Not specified'}</div>
            </div>

            {projectInfo.department && (
              <div className="flex items-start justify-between pt-2 border-t">
                <div className="text-sm font-medium text-muted-foreground">Department</div>
                <div className="text-sm text-right font-medium">
                  {typeof projectInfo.department === 'string' 
                    ? projectInfo.department 
                    : projectInfo.department.shortname || projectInfo.department.full_name || '—'}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Security Domain Health */}
      {questions.length > 0 && questions.some((q) => q.vulnerabilities?.length || quickScanResults[q.id]) && (
        <Card>
          <CardHeader>
            <CardTitle>Security Domain Health</CardTitle>
            <CardDescription>Overview of vulnerability distribution across security domains</CardDescription>
          </CardHeader>
          <CardContent>
            <DomainHealthGrid questions={questions} quickScanResults={quickScanResults} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
